import { WithholdingTransactionType } from "@wingspanhq/bookkeeping/dist/lib/interfaces/withholdingTransaction";
import {
  useIsMobile,
  useModalContext,
  WSButton,
  WSContainer,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSInfiniteScroll,
  WSStatus,
  WSStatusType,
  WSTable,
  WSTableColumn,
  WSTabs,
  WSText
} from "@wingspanhq/fe-component-library";
import { MemberWithholdingStatus } from "@wingspanhq/users/dist/lib/interfaces/member";
import { formatMoney } from "accounting";
import flatten from "lodash/flatten";
import moment from "moment";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { useUserId } from "../../query/hooks/helpers";
import { IFeatureFlags } from "../../query/hooks/useFeatureFlags";
import {
  useWithholdingBalance,
  useWithholdingTransactions
} from "../../query/taxes/queries";
import { useActivities, useMemberProfile } from "../../query/users/queries";
import { WSQueries } from "../../query/WSQuery";
import { openInNewTab } from "../../utils/openInNewTab";
import {
  ADD_TO_CALENDAR_LINK,
  getCurrentIRSQuarter
} from "../utils/getIRSQuarter";
import { PendingWithholdingList } from "./PendingWithholdingList";
import {
  TaxesModalTurnOnPayment,
  TURN_ON_PAYMENTS
} from "./TaxesModalTurnOnPayment";
import { TaxesModalTurnOnWithholdings } from "./TaxesModalTurnOnWithholdings";

const year = moment().year();

const quarters = [
  {
    title: `Q1: Jan 1 - Mar 31, ${year}`,
    date: `Due Apr 15, ${year}`,
    description: `Recommend sending payment Apr 1, ${year}`
  },
  {
    title: `Q2: Apr 1 - May 31, ${year}`,
    date: `Due Jun 15, ${year}`,
    description: `Recommend sending payment Jun 1, ${year}`
  },
  {
    title: `Q3: Jun 1 - Aug 31, ${year}`,
    date: `Due Sept 15, ${year}`,
    description: `Recommend sending payment Sept 1, ${year}`
  },
  {
    title: `Q4: Sept 1 - Dec 31, ${year}`,
    date: `Due Jan 15, ${year + 1}`,
    description: `Recommend sending payment Jan 1, ${year + 1}`
  }
];

export const columns: Array<WSTableColumn> = [
  {
    config: {
      header: {
        text: "Description"
      },
      gridTemplateSizeMax: "1fr"
    },
    renderFunction: row => (
      <>
        <WSText.Label singleLine color="grey700">
          {row.data.type === WithholdingTransactionType.TaxPayment
            ? "Payment to IRS"
            : row.data.invoiceNumber
            ? `Set aside from Invoice #${row.data.invoiceNumber}`
            : row.data.amount > 0
            ? "Deposit"
            : "Withdraw"}
        </WSText.Label>
        <WSText.Label singleLine color="grey700" formatDate="monthDayYear">
          {row.data.date}
        </WSText.Label>
      </>
    )
  },
  {
    config: {
      header: {
        text: "Amount"
      },
      justify: "end"
    },
    renderFunction: row => (
      <WSText.Label color="grey700" formatMoney="taxes">
        {row.data.amount}
      </WSText.Label>
    )
  }
];

const quarter = getCurrentIRSQuarter();

export const TaxesMainMobile: React.FC<{
  featureFlags: IFeatureFlags;
}> = () => {
  const history = useHistory();
  const isMobile = useIsMobile();
  const { openModal } = useModalContext();
  const memberId = useUserId();
  const qMemberProfile = useMemberProfile(memberId);
  const qWithholdingBalance = useWithholdingBalance({
    forceFetchOnMount: true
  });
  const qWithholdingTransactions = useWithholdingTransactions();
  const activityQuery = useActivities(memberId);

  useBrowserPageTitle("Taxes");

  const currentDueDate = moment(quarter.dueDate, "MMM DD, YYYY");
  const deadlineFromNow = currentDueDate.fromNow(true);

  return (
    <WSQueries
      queries={{
        member: qMemberProfile,
        balance: qWithholdingBalance,
        transactions: qWithholdingTransactions,
        activityQuery
      }}
    >
      {({
        member,
        balance,
        transactions,
        activityQuery: { data: activity }
      }) => {
        const withholdingTax = member.data.profile?.withholdings?.tax;
        const taxWithholdingBalance = balance.data.taxWithholdingBalance;
        const withholdingEnabled =
          withholdingTax?.status === MemberWithholdingStatus.Active;
        const transactionList = flatten(transactions.data || []);

        const tableData = transactionList.map(transaction => {
          return {
            data: {
              source: transaction,
              invoiceNumber: transaction.invoiceNumber,
              type: transaction.type,
              description: transaction.invoiceNumber,
              date: transaction.createdAt,
              amount: transaction.amount * -1
            },
            id: transaction.withholdingTransactionId
          };
        });

        return (
          <WSContainer verticalPadding data-testid="taxesDashboard">
            <TaxesModalTurnOnPayment />
            <TaxesModalTurnOnWithholdings />
            <WSFlexBox.CenterY justify="space-between">
              <WSText.Heading1>Tax Withholdings</WSText.Heading1>
              <WSStatus
                status={
                  withholdingEnabled
                    ? WSStatusType.Success
                    : WSStatusType.Warning
                }
              >
                <WSText.Label>
                  Withholdings {withholdingEnabled ? "on. " : "off. "}
                  <WSButton.Link
                    data-testid="withholdingsSwitcher"
                    inline
                    onClick={() => {
                      activity.flows.paymentsSetup?.complete
                        ? openModal("turn-on-withholdings")
                        : openModal(TURN_ON_PAYMENTS);
                    }}
                  >
                    {withholdingEnabled ? "Turn off." : "Turn on."}
                  </WSButton.Link>
                </WSText.Label>
              </WSStatus>
            </WSFlexBox.CenterY>

            <WSTabs
              mt="M"
              tabs={[
                {
                  label: "Withholdings",
                  render: () => (
                    <>
                      <WSText.Label color="grey600" mb="XS" align="center">
                        Current balance set aside for taxes
                      </WSText.Label>
                      <WSText.Display mb="XS" align="center">
                        {formatMoney(taxWithholdingBalance * -1)}
                      </WSText.Display>
                      <WSFlexBox.CenterX my="S">
                        <WSText.Heading5 inline align="center">
                          Next quarterly payment due {isMobile ? <br /> : null}{" "}
                          in {deadlineFromNow} (
                          <b>{currentDueDate.format("MMM DD")}</b>).
                          <WSIcon
                            name="calendar"
                            size="S"
                            ml="XS"
                            mb="-XS"
                            color="blue500"
                            onClick={() => openInNewTab(ADD_TO_CALENDAR_LINK)}
                          />
                        </WSText.Heading5>
                      </WSFlexBox.CenterX>
                      <WSGrid.Container>
                        <WSGrid.Item XS={6} S={6}>
                          <WSButton.Primary
                            fullWidth
                            onClick={() =>
                              history.push("/member/taxes/sent-to-irs")
                            }
                          >
                            Send to IRS
                          </WSButton.Primary>
                        </WSGrid.Item>

                        <WSGrid.Item XS={6} S={6}>
                          <WSButton.Secondary
                            fullWidth
                            onClick={() =>
                              history.push("/member/taxes/deposit")
                            }
                          >
                            Deposit
                          </WSButton.Secondary>
                        </WSGrid.Item>
                        <WSGrid.Item XS={6} S={6}>
                          <WSButton.Secondary
                            fullWidth
                            onClick={() =>
                              history.push("/member/taxes/withdraw")
                            }
                          >
                            Withdraw
                          </WSButton.Secondary>
                        </WSGrid.Item>
                      </WSGrid.Container>
                      <PendingWithholdingList />

                      <WSText.Heading4 my="M">
                        Posted Transactions
                      </WSText.Heading4>
                      <WSInfiniteScroll
                        onLoad={() => {
                          transactions.fetchMore();
                        }}
                        loadMore={transactionList.length > 0}
                        endOfList={!transactions.canFetchMore}
                        loading={!!transactions.isFetchingMore}
                      >
                        <WSTable
                          mt="M"
                          tableData={tableData}
                          columns={columns}
                          showHeader={false}
                          bottomMessage={
                            !transactionList.length && "No activity yet"
                          }
                        />
                      </WSInfiniteScroll>
                    </>
                  )
                },
                {
                  label: "Tax rate",
                  render: () => (
                    <>
                      <WSText.Label color="grey600" mb="XS" align="center">
                        My withholding rate is
                      </WSText.Label>
                      <WSText.Display mb="XS" align="center">
                        {withholdingTax?.rate}%
                      </WSText.Display>
                      <WSText.Heading5 color="grey600" mb="M" align="center">
                        Most accurate estimate based on tax profile
                      </WSText.Heading5>
                      <WSButton.Secondary
                        fullWidth
                        onClick={() =>
                          history.push("/member/taxes/edit/tax-rate")
                        }
                      >
                        Update tax profile
                      </WSButton.Secondary>
                      <WSText.Label my="M">
                        This is the proportion of 1099 income you should set
                        aside to be financially prepared for taxes.
                      </WSText.Label>
                      <WSText.Heading4 my="M">
                        {year} quarterly estimated payment schedule
                      </WSText.Heading4>
                      {quarters.map(({ title, description, date }) => (
                        <WSElement pb="S">
                          <WSDivider mb="S" />
                          <WSFlexBox.CenterY justify="space-between">
                            <WSText.Paragraph color="grey600">
                              {title}
                            </WSText.Paragraph>
                            <WSText.Paragraph color="grey700">
                              {date}
                            </WSText.Paragraph>
                          </WSFlexBox.CenterY>
                          <WSText.Label mt="XS" color="grey600">
                            {description}
                          </WSText.Label>
                        </WSElement>
                      ))}

                      <WSButton.Link
                        ml="S"
                        onClick={() => {
                          openInNewTab(ADD_TO_CALENDAR_LINK);
                        }}
                      >
                        Subscribe to Tax Calendar
                      </WSButton.Link>
                    </>
                  )
                }
              ]}
            />
          </WSContainer>
        );
      }}
    </WSQueries>
  );
};
